import { Contact, IContact } from '@app/models/Contact'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum ConnectionsScope {
  All = 'All',
  Mine = 'Mine',
}

export interface IConnectionsListState {
  readonly connectionType: string
  readonly error?: string
  readonly filterText: string
  readonly group: string
  readonly isLoading: boolean
  readonly items: List<IContact>
  readonly scope: ConnectionsScope
  readonly sortOrder: string
  readonly totalItems: number
}

const ConnectionsListStateRecord = Record({
  connectionType: 'All',
  error: undefined,
  filterText: '',
  group: '',
  isLoading: false,
  items: List<Contact>(),
  scope: ConnectionsScope.Mine,
  sortOrder: 'LastName',
  totalItems: 0,
})

export class ConnectionsListState extends ConnectionsListStateRecord
  implements IConnectionsListState {
  public static fromJS(initialState?: Partial<IConnectionsListState>) {
    if (!initialState) {
      return new ConnectionsListState()
    }

    const { group, items, ...rest } = initialState
    return new ConnectionsListState({
      items: items ? List(items.map((i) => Contact.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly connectionType: string
  public readonly error?: string
  public readonly filterText: string
  public readonly group: string
  public readonly isLoading: boolean
  public readonly items: List<Contact>
  public readonly scope: ConnectionsScope
  public readonly sortOrder: string
  public readonly totalItems: number

  constructor(props?: Partial<ConnectionsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  LoadConnections = 'CONNECTIONS_LIST::LOAD_CONNECTIONS',
  LoadConnectionsFailure = 'CONNECTIONS_LIST::LOAD_CONNECTIONS_FAILURE',
  LoadConnectionsRequest = 'CONNECTIONS_LIST::LOAD_CONNECTIONS_REQUEST',
  LoadConnectionsSuccess = 'CONNECTIONS_LIST::LOAD_CONNECTIONS_SUCCESS',
  SetConnectionType = 'CONNECTIONS_LIST::SET_CONNECTION_TYPE',
  SetFilterText = 'CONNECTIONS_LIST::SET_FILTER_TEXT',
  SetGroup = 'CONNECTION_LIST::SET_GROUP',
  SetScope = 'CONNECTIONS_LIST::SET_SCOPE',
  SetSortOrder = 'CONNECTIONS_LIST::SET_SORT_ORDER',
}

interface ILoadConnectionsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ActionTypes.LoadConnections
}

interface ILoadConnectionsFailureAction {
  error: string
  type: ActionTypes.LoadConnectionsFailure
}

interface ILoadConnectionsRequestAction {
  type: ActionTypes.LoadConnectionsRequest
}

interface ILoadConnectionsSuccessAction {
  append: boolean
  items: Contact[]
  totalItems: number
  type: ActionTypes.LoadConnectionsSuccess
}

interface ISetConnectionTypeAction {
  connectionType: string
  type: ActionTypes.SetConnectionType
}

interface ISetFilterTextAction {
  filter: string
  type: ActionTypes.SetFilterText
}

interface ISetGroupAction {
  group: string
  type: ActionTypes.SetGroup
}

interface ISetScopeAction {
  scope: ConnectionsScope
  type: ActionTypes.SetScope
}

interface ISetSortOrderAction {
  sortOrder: string
  type: ActionTypes.SetSortOrder
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadConnectionsFailureAction
  | ILoadConnectionsRequestAction
  | ILoadConnectionsSuccessAction
  | ISetConnectionTypeAction
  | ISetFilterTextAction
  | ISetGroupAction
  | ISetScopeAction
  | ISetSortOrderAction

// -------------------------
// ACTION CREATORS
// -------------------------
function loadConnections(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadConnectionsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: ActionTypes.LoadConnections,
  }
}

function loadConnectionsFailure(error: string): ILoadConnectionsFailureAction {
  return {
    error,
    type: ActionTypes.LoadConnectionsFailure,
  }
}

function loadConnectionsRequest(): ILoadConnectionsRequestAction {
  return {
    type: ActionTypes.LoadConnectionsRequest,
  }
}

function loadConnectionsSuccess(
  items: Contact[],
  append: boolean,
  totalItems: number,
): ILoadConnectionsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ActionTypes.LoadConnectionsSuccess,
  }
}

function setConnectionType(connectionType: string): ISetConnectionTypeAction {
  return {
    connectionType,
    type: ActionTypes.SetConnectionType,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: ActionTypes.SetFilterText,
  }
}

function setGroup(group: string): ISetGroupAction {
  return {
    group,
    type: ActionTypes.SetGroup,
  }
}

function setScope(scope: ConnectionsScope): ISetScopeAction {
  return {
    scope,
    type: ActionTypes.SetScope,
  }
}

function setSortOrder(sortOrder: string) {
  return {
    sortOrder,
    type: ActionTypes.SetSortOrder,
  }
}

export const actionCreators = {
  loadConnections,
  setConnectionType,
  setFilterText,
  setGroup,
  setScope,
  setSortOrder,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<ConnectionsListState> = (
  state: ConnectionsListState = new ConnectionsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.LoadConnectionsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as ConnectionsListState

    case ActionTypes.LoadConnectionsRequest:
      return state.set('isLoading', true) as ConnectionsListState

    case ActionTypes.LoadConnectionsSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<Contact>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as ConnectionsListState

    case ActionTypes.SetConnectionType:
      return state.set(
        'connectionType',
        action.connectionType,
      ) as ConnectionsListState

    case ActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as ConnectionsListState

    case ActionTypes.SetGroup:
      return state.set('group', action.group) as ConnectionsListState

    case ActionTypes.SetScope:
      return state.set('scope', action.scope) as ConnectionsListState

    case ActionTypes.SetSortOrder:
      return state.set('sortOrder', action.sortOrder) as ConnectionsListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadConnectionsSaga(action: ILoadConnectionsAction) {
  try {
    yield put(loadConnectionsRequest())

    const currentState: ConnectionsListState = yield select(
      (state: ApplicationState) => state.connectionsList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (currentState.connectionType !== 'All') {
      query.push(`type=${currentState.connectionType}`)
    }

    if (currentState.sortOrder !== 'LastName') {
      query.push(`sort=${currentState.sortOrder}`)
    }

    if (currentState.group) {
      query.push(`group=${encodeURIComponent(currentState.group)}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl =
      currentState.scope === ConnectionsScope.All
        ? '/api/Contacts'
        : '/api/Contacts/My'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: Contact[] = result.results.map((c: any) =>
        Contact.fromJS(c),
      )
      yield put(
        loadConnectionsSuccess(contacts, action.append, result.paging.total),
      )
    } else {
      yield put(loadConnectionsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadConnectionsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadConnections() {
  yield takeLatest(ActionTypes.LoadConnections, loadConnectionsSaga)
}

export function* connectionsListRootSaga() {
  yield fork(watchLoadConnections)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): ConnectionsListState => state.connectionsList

export const selectConnectionType = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.connectionType,
  )

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectGroup = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.group,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectScope = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.scope,
  )

export const selectSortOrder = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.sortOrder,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
